import axios from "axios";

export type ApiFailureKind =
  | "cancelled"
  | "transport"
  | "timeout"
  | "rate_limited"
  | "server"
  | "client";

export type ClassifiedApiError = Error & {
  apiFailureKind?: ApiFailureKind;
  cause?: unknown;
  code?: string;
};

export const getApiFailureKind = (error: unknown): ApiFailureKind => {
  if (
    error instanceof Error &&
    "apiFailureKind" in error &&
    typeof (error as ClassifiedApiError).apiFailureKind === "string"
  ) {
    return (error as ClassifiedApiError).apiFailureKind!;
  }
  if (!axios.isAxiosError(error)) return "client";
  if (error.code === "ERR_CANCELED") return "cancelled";
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") return "timeout";
  if (!error.response) return "transport";
  if (error.response.status === 429) return "rate_limited";
  if (error.response.status >= 500) return "server";
  return "client";
};

type ApiErrorBody = {
  message?: unknown;
  error?: unknown;
  code?: unknown;
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) return fallback;
  const responseData = error.response?.data;
  if (
    responseData &&
    typeof responseData === "object" &&
    "message" in responseData &&
    typeof responseData.message === "string"
  ) return responseData.message;
  if (
    responseData &&
    typeof responseData === "object" &&
    "error" in responseData &&
    typeof responseData.error === "string"
  ) return responseData.error;
  return fallback;
};

export const createApiError = (error: unknown, fallback: string): ClassifiedApiError => {
  const responseData =
    typeof error === "object" && error !== null && "response" in error
      ? (error as { response?: { data?: ApiErrorBody } }).response?.data
      : undefined;
  const message =
    (typeof responseData?.message === "string" && responseData.message) ||
    (typeof responseData?.error === "string" && responseData.error) ||
    fallback;
  const apiError = new Error(message) as ClassifiedApiError;
  apiError.apiFailureKind = getApiFailureKind(error);
  apiError.cause = error;

  const existingCode =
    typeof error === "object" && error !== null && "code" in error
      ? (error as { code?: unknown }).code
      : undefined;
  if (typeof responseData?.code === "string") {
    apiError.code = responseData.code;
  } else if (typeof existingCode === "string") {
    apiError.code = existingCode;
  }

  return apiError;
};

export const isRetryableApiError = (error: unknown): boolean => {
  const kind = getApiFailureKind(error);
  return kind === "transport" || kind === "timeout" || kind === "server" || kind === "rate_limited";
};
